import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import HomePage from './pages/HomePage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import ResearchPage from './research/ResearchPage';
import AboutPage from './pages/AboutPage';
import ProductPage from './pages/ProductPage';
import LoginPage from './authentication/LoginPage';
import BetaPage from './pages/BetaPage';
import OurPersonas from './pages/PersonasPage';
import FeedbackPage from './pages/FeedbackPage';
import CreditsPage from './payment/CreditsPage';
import PaymentSuccess from './payment/PaymentSuccess';
import PaymentCancelled from './payment/PaymentCancelled';
import ResearchRequestPage from './pages/ResearchRequestPage';
import ExampleResearchPage from './research/ExampleResearchPage';
import ExampleResearch from './research/ExampleResearch'; 
import DigitalCustomerTwinPanel from './pages/PrivateDigitalTwinPanel';
import AIInstantMarketInsights from './pages/AIInstantMarketInsights';
import StartupValidationPage from './pages/usecases/StartupValidationPage';
import BrandingFeedbackPage from './pages/usecases/BrandingFeedbackPage';
import CampaignValidationPage from './pages/usecases/CampaignValidationPage';
import UseCasesIndexPage from './pages/usecases/UseCasesIndexPage';
import PollsPage from './pages/PollsPage';
import AdminPollsPage from './pages/AdminPollsPage';
import BetaAdminPage from './pages/BetaAdminPage'; // New import for BetaAdminPage
import { StripeWrapper } from './components/StripeWrapper';

import FAQ from './pages/FAQ';
import AccountPage from './pages/AccountPage';
import CareersPage from './pages/CareersPage';
import { AuthProvider, useAuth } from './authentication/AuthContext';
import PrivateRoute from './authentication/PrivateRoute';
// import './i18n';
// import { useTranslation } from 'react-i18next';
import { pageview, analyticsService } from './services/analyticsService';
import FeedbackWidget from './components/FeedbackWidget';

import SharedResearchViewer from './research/SharedResearchViewer'; // Import new component

// Route change tracker component
function RouteChangeTracker() {
  const location = useLocation();
  const { user } = useAuth();
  
  useEffect(() => {
    // Capture UTM parameters on initial load
    analyticsService.captureUtmParams();
    
    // Track page visit
    analyticsService.trackEvent(
      user?.username || null, 
      'page_visit', 
      { page: window.location.pathname }
    );
    
    // Track page view on location change
    pageview(location.pathname + location.search);
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location, user]);
  
  return null;
}

function App() {
  const { user } = useAuth();
  // const { t, i18n } = useTranslation();

  useEffect(() => {
    // Capture UTM parameters on initial load
    analyticsService.captureUtmParams();
    
    // Track initial page visit
    analyticsService.trackEvent(
      user?.username || null,
      'page_visit',
      { page: window.location.pathname }
    );
  }, [user]);

  // useEffect(() => {
  //   i18n.changeLanguage(navigator.language);
  // }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <AuthProvider>
      <Router>
        <div className="app">
          <Header />
          <RouteChangeTracker />
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/ourpersonas" element={<OurPersonas />} />
              <Route path="/blogs" element={<BlogPage />} />
              <Route path="/blogs/:blogId" element={<BlogDetailPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/product" element={<ProductPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/become-beta" element={<BetaPage />} />
              <Route path="/feedback" element={<FeedbackPage />} />              
              <Route path="/polls" element={<PollsPage />} />
              <Route path="/pollsadmin" element={<PrivateRoute adminOnly={true}><AdminPollsPage /></PrivateRoute>} />
              <Route path="/admin/beta" element={<BetaAdminPage />} /> {/* Added admin beta management route */}
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/payment-cancelled" element={<PaymentCancelled />} />
              <Route path="/FAQ" element={<FAQ />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/researchrequest" element={<ResearchRequestPage />} />
              <Route path="/research" element={<PrivateRoute requiresResearch={true}><ResearchPage /></PrivateRoute>} />
              <Route path="/account" element={<PrivateRoute><AccountPage /></PrivateRoute>} />
              <Route path="/credits" element={<PrivateRoute><StripeWrapper><CreditsPage /></StripeWrapper></PrivateRoute>} />
              <Route path="/exampleresearches" element={<ExampleResearchPage />} />
              <Route path="/exampleresearch" element={<ExampleResearch />} />
              <Route path="/exampleresearch/:researchId" element={<ExampleResearch />} />
              <Route path="/use-cases" element={<UseCasesIndexPage />} />
              <Route path="/use-cases/startup-validation" element={<StartupValidationPage />} />
              <Route path="/use-cases/branding-feedback" element={<BrandingFeedbackPage />} />
              <Route path="/use-cases/campaign-validation" element={<CampaignValidationPage />} />
              <Route path="/private-digital-twin-panel" element={<DigitalCustomerTwinPanel />} />
              <Route path="/ai-instant-market-insights" element={<AIInstantMarketInsights />} />
              {/* Public Shared Link Route */}
              <Route path="/shared/:shareID" element={<SharedResearchViewer />} />
            </Routes>
          </main>
          <ConditionalFooter />
          <CookieConsent />
          <FeedbackWidget />
        </div>
      </Router>
    </AuthProvider>
    </GoogleOAuthProvider>
  );
}

function ConditionalFooter() {
  const location = useLocation();

  if (location.pathname === '/research' || location.pathname.startsWith('/shared')) {
    return null;
  }
  return <Footer />;
}

export default App;